import type { BestNextMoveBundle, PrimarySurface, ProjectMissionSnapshot, ProjectProgressSnapshot, ProjectType } from '../../contracts/types'
import { FIREBASE_ENABLED, getDbOrThrow } from '@/lib/firebase'

// Firestore sync read is intentionally unavailable in the live bundle path.

function parseProjectType(value: unknown): ProjectType {
  const allowed: ProjectType[] = [
    'film_story',
    'film_xr_expandable',
    'game_interactive',
    'music_video',
    'timeline_production',
    'scape_first',
    'hybrid_transmedia',
    'studio_pack',
  ]
  if (typeof value === 'string' && allowed.includes(value as ProjectType)) {
    return value as ProjectType
  }
  return 'film_story'
}

function parsePrimarySurface(value: unknown): PrimarySurface | undefined {
  const allowed: PrimarySurface[] = [
    'guided_studio',
    'omnistudio',
    'omnicine',
    'omnicast',
    'scapes_worlds',
    'xr_worlds',
    'none',
  ]
  if (typeof value === 'string' && allowed.includes(value as PrimarySurface)) {
    return value as PrimarySurface
  }
  return undefined
}

/**
 * Read-only Firestore project load. Returns null when missing, disabled, or invalid — never ORBIS fallback.
 */
export function readFirestoreProjectMissionSnapshot(
  _projectId: string
): ProjectMissionSnapshot | null {
  // Sync live bundle uses explicit registry entries; use readFirestoreProjectMissionSnapshotAsync when async route wiring ships.
  return null
}

/** Async read for future route loading (not wired to sync bundle yet). */
export async function readFirestoreProjectMissionSnapshotAsync(
  projectId: string
): Promise<ProjectMissionSnapshot | null> {
  if (!FIREBASE_ENABLED || !projectId.trim()) return null

  try {
    const { doc, getDoc } = await import('firebase/firestore')
    const db = getDbOrThrow()
    const snap = await getDoc(doc(db, 'projects', projectId))
    if (!snap.exists()) return null

    const data = snap.data() as Record<string, unknown>
    const id = typeof data.projectId === 'string' ? data.projectId : projectId
    if (id !== projectId) return null

    const title = typeof data.title === 'string' ? data.title : projectId
    const progress = data.progress as ProjectProgressSnapshot | undefined
    const bestNextMove = data.bestNextMove as BestNextMoveBundle | undefined
    if (!progress || !bestNextMove) return null

    return {
      projectId: id,
      title,
      projectType: parseProjectType(data.projectType),
      progress: { ...progress, projectId: id, projectName: title },
      bestNextMove,
      activeSurface: parsePrimarySurface(data.activeSurface),
      ownerUid: typeof data.ownerUid === 'string' ? data.ownerUid : undefined,
    }
  } catch {
    return null
  }
}
